import React, { useEffect, useState } from 'react';
import { IconButton, Link, Panel, Stack, Text, Toggle } from '@fluentui/react';
import IvicosStrings from 'kits/language/stringKit';
import { INTEGRATION_LABEL_STYLE } from '../integrationLayout';
import { OrganizationDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import { ZammadDeactivationModal } from './ZammadDeactivationModal';
import type { IZammadConfiguration } from './zammad.types';
import { resolveZammadErrorMessage } from './ZammadSettingsForm';
import { ZammadSettingsForm } from './ZammadSettingsForm';

const ZAMMAD_WEBSITE_URL = 'https://zammad.com';

interface IZammadSettingsProps {
    isZammadEnabled: boolean;
    zammadBaseUrl: string;
    updateZammadConfig: (config: IZammadConfiguration) => Promise<OrganizationDto>;
    isOrgLoaded: boolean;
}

export const ZammadSettings: React.FC<IZammadSettingsProps> = ({ isZammadEnabled, zammadBaseUrl, updateZammadConfig, isOrgLoaded }) => {
    const [isToggleOn, setIsToggleOn] = useState(isZammadEnabled);
    const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
    const [isDeactivationModalVisible, setIsDeactivationModalVisible] = useState(false);
    const [deactivationErrorMessage, setDeactivationErrorMessage] = useState<string | null>(null);

    // Keep the local toggle in sync with the backend-derived state (e.g. after a save/disable
    // round-trip, or another manager changing the setting).
    useEffect(() => {
        setIsToggleOn(isZammadEnabled);
    }, [isZammadEnabled]);

    const handleToggleChange = (_ev: React.MouseEvent<HTMLElement>, checked?: boolean): void => {
        setDeactivationErrorMessage(null);
        if (checked) {
            // Not yet configured on the backend - open the panel so the user can supply the
            // base URL + access token; the toggle only becomes backend-true once Save succeeds.
            setIsToggleOn(true);
            return;
        }
        if (isZammadEnabled) {
            setIsDeactivationModalVisible(true);
        } else {
            setIsToggleOn(false);
        }
    };

    const handleConfirmDeactivate = async (): Promise<void> => {
        setIsDeactivationModalVisible(false);
        setDeactivationErrorMessage(null);
        try {
            // Empty object clears Zammad config on the backend without re-running credential
            // verification (that path is for the enable/Save flow). Sending is_zammad_enable:
            // false with empty credentials previously tripped verification and returned 422.
            await updateZammadConfig({});
        } catch (err) {
            console.error('Failed to disable Zammad integration:', err);
            setDeactivationErrorMessage(resolveZammadErrorMessage(err));
        }
    };

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 12 }}>
                <Stack style={INTEGRATION_LABEL_STYLE}>
                    <Link href={ZAMMAD_WEBSITE_URL} target="_blank" rel="noopener noreferrer" style={{ fontWeight: 600 }} data-test-id="zammad-website-link">
                        {IvicosStrings.zammadSettingsToggleLabel}
                    </Link>
                </Stack>
                <Stack.Item grow />
                <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                    <Toggle
                        checked={isToggleOn}
                        onText={IvicosStrings.providerEnabledLabel}
                        offText={IvicosStrings.providerDisabledLabel}
                        disabled={!isOrgLoaded}
                        onChange={handleToggleChange}
                        styles={{ root: { minWidth: 120, marginBottom: 0 } }}
                        data-test-id="enable-zammad-integration"
                    />
                    {isToggleOn && (
                        <>
                            <IconButton iconProps={{ iconName: 'Settings' }} onClick={() => setIsSettingsPanelOpen(true)} data-test-id="zammad-settings-icon" />
                            {isZammadEnabled ? (
                                <Text style={{ color: '#107C10', fontWeight: 600 }}>{IvicosStrings.zammadSettingsConfiguredStatus}</Text>
                            ) : (
                                <Text style={{ color: '#A80000', fontWeight: 600 }}>{IvicosStrings.zammadSettingsNotConfiguredStatus}</Text>
                            )}
                        </>
                    )}
                </Stack>
            </Stack>
            {deactivationErrorMessage && (
                <Text style={{ color: '#A80000' }} data-test-id="zammad-deactivation-error">
                    {deactivationErrorMessage}
                </Text>
            )}
            <Panel
                headerText={IvicosStrings.zammadSettingsToggleLabel}
                isOpen={isSettingsPanelOpen}
                onDismiss={() => setIsSettingsPanelOpen(false)}
                closeButtonAriaLabel="Close"
                data-test-id="zammad-settings-panel"
            >
                <ZammadSettingsForm
                    zammadBaseUrl={zammadBaseUrl}
                    isOpen={isSettingsPanelOpen}
                    updateZammadConfig={updateZammadConfig}
                    onSaveSuccess={() => setIsSettingsPanelOpen(false)}
                    onCancel={() => setIsSettingsPanelOpen(false)}
                />
            </Panel>
            <ZammadDeactivationModal
                isOpen={isDeactivationModalVisible}
                onCancel={() => setIsDeactivationModalVisible(false)}
                onConfirm={handleConfirmDeactivate}
            />
        </Stack>
    );
};
